import { Container, Sprite } from "pixi.js";
import { getComponent, type Entity } from "../world/Entity";
import type { CharacterComponent, ClothingSlot } from "../world/components";
import type { AssetLoader } from "../assets/AssetLoader";
import type { AnimationTarget } from "../animation/animations";
import type { EntityDefinitionRegistry } from "../game-content/EntityDefinitionRegistry";

const PLACEHOLDER_TINT = 0xff66cc;

// Draw order for clothing layers, topmost last. Base body layers (from `character.layers`) always draw first.
const CLOTHING_DRAW_ORDER: readonly ClothingSlot[] = ["shoes", "bottom", "shirt", "accessory", "hat", "heldItem"];

/** Composes a character's body + clothing layers into one container instead of one baked sprite per outfit. */
export class CharacterView implements AnimationTarget {
  readonly container = new Container();
  private readonly layerSprites = new Map<string, Sprite>();

  constructor(
    entity: Entity,
    private readonly assets: AssetLoader,
    private readonly entityDefinitions: EntityDefinitionRegistry,
  ) {
    this.container.sortableChildren = true;
    this.sync(entity);
  }

  sync(entity: Entity): void {
    this.container.position.set(entity.transform.position.x, entity.transform.position.y);
    this.container.visible = entity.visible;
    this.container.zIndex = entity.transform.position.y + 1;

    const character = getComponent(entity, "character");
    if (!character) return;
    this.syncLayers(this.layerKeysFor(character));
  }

  private layerKeysFor(character: CharacterComponent): readonly string[] {
    const clothingKeys = CLOTHING_DRAW_ORDER.map((slot) => character.clothing[slot]).filter(
      (key): key is string => key !== undefined,
    );
    return [...character.layers, ...clothingKeys];
  }

  private syncLayers(assetKeys: readonly string[]): void {
    for (const key of [...this.layerSprites.keys()]) {
      if (!assetKeys.includes(key)) {
        this.layerSprites.get(key)?.destroy();
        this.layerSprites.delete(key);
      }
    }
    const missing = assetKeys.filter((key) => !this.layerSprites.has(key));
    if (missing.length > 0) this.assets.loadKeys(missing);
    assetKeys.forEach((key, index) => {
      const { texture, isPlaceholder } = this.assets.textureFor(key);
      let sprite = this.layerSprites.get(key);
      if (!sprite) {
        sprite = new Sprite({ texture, anchor: { x: 0.5, y: 1 } });
        this.layerSprites.set(key, sprite);
        this.container.addChild(sprite);
      } else {
        sprite.texture = texture;
      }
      sprite.tint = isPlaceholder ? PLACEHOLDER_TINT : 0xffffff;
      sprite.zIndex = index;
      const wearable = this.entityDefinitions.get(key)?.components.find((c) => c.type === "wearable");
      sprite.position.set(wearable?.offset?.x ?? 0, wearable?.offset?.y ?? 0);
    });
  }

  destroy(): void {
    this.container.destroy({ children: true });
  }

  get x(): number {
    return this.container.x;
  }
  set x(v: number) {
    this.container.x = v;
  }
  get y(): number {
    return this.container.y;
  }
  set y(v: number) {
    this.container.y = v;
  }
  get scaleX(): number {
    return this.container.scale.x;
  }
  set scaleX(v: number) {
    this.container.scale.x = v;
  }
  get scaleY(): number {
    return this.container.scale.y;
  }
  set scaleY(v: number) {
    this.container.scale.y = v;
  }
  get rotation(): number {
    return this.container.rotation;
  }
  set rotation(v: number) {
    this.container.rotation = v;
  }
  get alpha(): number {
    return this.container.alpha;
  }
  set alpha(v: number) {
    this.container.alpha = v;
  }
}
